import moment from 'moment/min/moment-with-locales';

import localStorageUtil from './localStorageUtil';
import { normalizeLanguageCode } from './language-util';

// ----------------------------------------------------------------------

type InputValue = number | string | Date | undefined | null;

const FULL_DATE_WITH_WEEKDAY_KO = 'YYYY년 M월 D일 dddd';
const FULL_DATE_WITH_WEEKDAY_NON_KO = 'dddd, MMMM D, YYYY';

export function momentDate(date: InputValue, newFormat?: string) {
  const format = newFormat ?? 'L';
  return date ? moment(date).format(format) : '';
}

export function momentDay(date: InputValue, newFormat?: string) {
  const format = newFormat ?? 'DD';
  return date ? moment(date).format(format) : '';
}

export function momentMonth(date: InputValue, newFormat?: string) {
  const format = newFormat ?? 'MMM';
  return date ? moment(date).format(format) : '';
}

export function momentYear(date: InputValue, newFormat?: string) {
  const format = newFormat ?? 'YY';
  return date ? moment(date).format(format) : '';
}

export function momentTime(date: InputValue, newFormat?: string) {
  const format = newFormat ?? 'HH:mm';
  return date ? moment(date).format(format) : '';
}

export function formatDateWithMoment(date: InputValue) {
  // iOS 형식을 안전하게 처리하는 새로운 함수 사용
  return safeFormatDateToYYYYMMDD(date);
}

/**
 * 날짜 값을 지정된 형식으로 포맷팅하여 반환하는 함수
 *
 * @param date - 입력 값 (Unix timestamp, ISO string, Date 객체 등)
 * @param newFormat - 선택적 포맷 문자열 (기본값: 'L HH:mm')
 * @returns 포맷팅된 날짜 문자열 또는 빈 문자열 (date가 falsy일 경우)
 */
export function momentDateTime(date: InputValue, newFormat?: string) {
  // 날짜 포맷 지정 ('L HH:mm' = 로케일 날짜 + 시간, 예: '2025. 8. 3. 20:30')
  const format = newFormat ?? 'L HH:mm';

  // date가 null, undefined, 또는 falsy한 값이면 빈 문자열 반환
  if (!date) return '';

  // 숫자 타입인지 여부 확인
  const isNumber = typeof date === 'number';

  // 숫자인 경우, 초 단위 Unix timestamp인지 확인
  // 9999999999(초 단위 기준, 약 2286년까지)보다 작으면 초 단위로 판단
  const isUnixSecond = isNumber && date < 9999999999;

  // 초 단위 timestamp일 경우 moment.unix()로 처리, 그렇지 않으면 moment(date)로 처리
  const momentObj = isUnixSecond ? moment.unix(date) : moment(date);

  // 지정된 포맷으로 문자열 반환
  return momentObj.format(format);
}

export function momentTimestamp(date: InputValue) {
  return date ? moment(date).valueOf() : 0;
}

export function momentToNow(date: InputValue) {
  return date ? moment(date).fromNow() : '';
}

export function momentUnix(data: number, newFormat?: string) {
  const format = newFormat ?? 'L';
  return data ? moment.unix(data).format(format) : '';
}

export function isBetween(inputDate: InputValue, startDate: Date, endDate: Date) {
  const date = moment(inputDate);
  const start = moment(startDate);
  const end = moment(endDate);
  return date.isBetween(start, end);
}
export function beforeToday(data: any): string {
  return moment.unix(data).fromNow();
}
export type DurationBreakdown = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

/**
 * 두 시각 차이를 일·시·분·초(총량 기준)로 분해한다. 표시용 문자열은 화면에서 i18n 단위와 조합한다.
 */
export function getDurationBreakdown(startDate: string, endDate: string): DurationBreakdown {
  const startMoment = moment(startDate);
  const endMoment = moment(endDate);

  if (!startMoment.isValid() || !endMoment.isValid()) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  let totalSeconds = Math.max(0, Math.floor(endMoment.diff(startMoment) / 1000));

  const days = Math.floor(totalSeconds / 86400);
  totalSeconds %= 86400;
  const hours = Math.floor(totalSeconds / 3600);
  totalSeconds %= 3600;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return { days, hours, minutes, seconds };
}
export function isAfter(startDate: InputValue, endDate: InputValue) {
  if (!startDate || !endDate) return false;

  const start = moment(startDate);
  const end = moment(endDate);

  return start.isAfter(end);
}

export function momentDateKo(date: InputValue, newFormat?: string, code: string = 'ko') {
  moment.locale(code);
  const format = newFormat ?? 'L';
  return date ? moment(date).format(format) : '';
}

export function momentInitialOfDay(date: InputValue, format: string = 'dd'): string {
  return moment(date).format(format);
}

export function momentTime12Hour(date: InputValue, newFormat: string = 'h:mm A') {
  return date ? moment(date).format(newFormat) : '';
}

/** languageInfo.code 정규화. 부재·corrupt JSON이면 null. */
function readLanguageInfoCode(): string | null {
  try {
    const languageInfo = localStorageUtil.getLanguageInfo();
    if (!languageInfo) return null;
    const parsed = JSON.parse(languageInfo);
    if (!parsed?.code || typeof parsed.code !== 'string') return null;
    return normalizeLanguageCode(parsed.code);
  } catch {
    return null;
  }
}

/**
 * languageInfo code 또는 (#F3) 현재 moment.locale()로 isKo/locale 결정.
 * 전역 moment.locale setter는 사용하지 않는다.
 */
function resolveFullDateLocale(code: string | null): { isKo: boolean; locale: string } {
  if (code != null) {
    const isKo = code === 'ko';
    return { isKo, locale: isKo ? 'ko' : code };
  }

  const currentLocale = moment.locale();
  if (!currentLocale || typeof currentLocale !== 'string') {
    // #E2: locale 판별 불가 시 non-ko + en
    return { isKo: false, locale: 'en' };
  }

  const isKo = normalizeLanguageCode(currentLocale) === 'ko';
  return { isKo, locale: isKo ? 'ko' : currentLocale };
}

/**
 * Q&A 채팅 날짜 헤더용. languageInfo.code 기준 locale-aware 포맷.
 * - ko / ko-KR → `YYYY년 M월 D일 dddd`
 * - languageInfo 명시 non-ko → `dddd, MMMM D, YYYY`
 * - 부재·corrupt JSON(#F3) → 현재 moment.locale()이 ko계열이면 ko 포맷, 아니면 non-ko
 * - newFormat 명시 시 locale 분기 없이 해당 포맷만 적용
 * - 전역 moment.locale 변이 없음 (인스턴스 .locale()만 사용)
 */
export function momentFullDateWithWeekday(date: InputValue, newFormat?: string) {
  if (!date) return '';
  if (newFormat !== undefined) return moment(date).format(newFormat);

  const { isKo, locale } = resolveFullDateLocale(readLanguageInfoCode());
  return moment(date)
    .locale(locale)
    .format(isKo ? FULL_DATE_WITH_WEEKDAY_KO : FULL_DATE_WITH_WEEKDAY_NON_KO);
}

export function momentToISOString(date: InputValue) {
  return date ? moment(date).toISOString() : '';
}

export function parseIosDateFormat(dateString: string): string | null {
  /**
   * iOS 포맷 매칭 정규식
   *
   * ^                                   : 문자열 시작 고정(anchor)
   * (\\d{4})                            : 그룹1 - 연도(year) 4자리 숫자
   * \\.                                 : 리터럴 점(.) — 메타문자이므로 백슬래시로 이스케이프
   * (\\d{1,2})                          : 그룹2 - 월(month) 1~2자리 숫자 (01~12뿐 아니라 1~12도 허용)
   * \\.                                 : 리터럴 점(.)
   * (\\d{1,2})                          : 그룹3 - 일(day) 1~2자리 숫자 (01~31뿐 아니라 1~31도 허용)
   * \\.                                 : 리터럴 점(.) — '2025.08.14.'의 마지막 점을 매칭
   * \\s*                                : 0개 이상의 공백(스페이스, 탭 등) — '14. 00:00' 처럼 점 뒤 공백 허용
   * (\\d{1,2})                          : 그룹4 - 시(hour) 1~2자리 숫자 (00~23 범위 검증은 정규식에서 하지 않음)
   * :                                   : 리터럴 콜론(:)
   * (\\d{1,2})                          : 그룹5 - 분(minute) 1~2자리 숫자 (00~59 범위 검증은 정규식에서 하지 않음)
   * $                                   : 문자열 끝 고정(anchor)
   *
   * 요약: 'YYYY.MM.DD. HH:mm' 패턴을 느슨하게 허용한다.
   *      (숫자 자리수는 맞지만, 실제 달/일/시/분의 범위는 moment가 최종 검증)
   */
  const regexp = /^(\d{4})\.(\d{1,2})\.(\d{1,2})\.\s*(\d{1,2}):(\d{1,2})$/;
  const iosMatch = regexp.exec(dateString);
  if (iosMatch) {
    const [, year, month, day, hour, minute] = iosMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
  }
  return null;
}

/**
 * 다양한 플랫폼에서 전달되는 날짜 형식을 안전하게 파싱하여 Date 객체로 변환
 * @param dateString 날짜 문자열 (iOS: '2025.08.14. 00:00', 표준: ISO string 등)
 * @returns Date 객체 또는 null (유효하지 않은 경우)
 */
export function safeParseDateString(dateString: InputValue): Date | null {
  if (!dateString) {
    return null;
  }

  try {
    // moment.js를 사용하여 다양한 형식의 날짜 문자열을 파싱
    const momentObj = moment(dateString);

    // moment 객체가 유효한지 확인, iOS 특수 형식 '2025.08.14. 00:00' 처리
    if (!momentObj.isValid() && typeof dateString === 'string') {
      const iosFormatted = parseIosDateFormat(dateString);

      if (iosFormatted) {
        const retryMoment = moment(iosFormatted);
        return retryMoment.isValid() ? retryMoment.toDate() : null;
      }
      return null;
    }

    return momentObj.toDate();
  } catch (error) {
    console.warn('Failed to parse date string:', dateString, error);
    return null;
  }
}

/**
 * iOS 날짜 형식을 포함한 다양한 날짜 형식을 YYYYMMDD로 안전하게 변환
 * @param dateString 날짜 문자열 (iOS: '2025.08.30. 00:00', 표준: ISO string 등)
 * @returns YYYYMMDD 형식의 문자열 또는 빈 문자열 (유효하지 않은 경우)
 */
export function safeFormatDateToYYYYMMDD(dateString: InputValue): string {
  if (!dateString) {
    return '';
  }

  try {
    // 먼저 표준 moment 파싱 시도
    let momentObj = moment(dateString);

    // moment 객체가 유효하지 않은 경우 iOS 형식 처리
    if (!momentObj.isValid() && typeof dateString === 'string') {
      /**
       * 'YYYY.MM.DD. HH:mm' 형태를 폭넓게 허용한다.
       * - 자릿수만 검증하고 실제 범위(예: 00~23시, 00~59분)는 moment에 위임.
       */
      const iosFormatted = parseIosDateFormat(dateString);
      if (iosFormatted) {
        momentObj = moment(iosFormatted);
      }
    }

    // 여전히 유효하지 않으면 빈 문자열 반환
    if (!momentObj.isValid()) {
      console.warn('Invalid date format:', dateString);
      return '';
    }

    // YYYYMMDD 형식으로 반환
    return momentObj.format('YYYYMMDD');
  } catch (error) {
    console.warn('Failed to format date to YYYYMMDD:', dateString, error);
    return '';
  }
}

/**
 * 체크리스트 DATE 문항 답변을 UI 한 줄로 표시한다.
 * - 저장/전송 형식(YYYYMMDD)이면 로케일 날짜(L)로 변환
 * - 그 외(Date 객체, ISO 등)는 safeFormatDateToYYYYMMDD 후 L, 실패 시 문자열 그대로
 */
export function formatChecklistDateAnswerForUi(raw: InputValue): string {
  if (raw === undefined || raw === null || raw === '') {
    return '';
  }
  if (typeof raw === 'string' && /^\d{8}$/.test(raw.trim())) {
    return moment(raw.trim(), 'YYYYMMDD', true).format('L');
  }
  const ymd = safeFormatDateToYYYYMMDD(raw);
  if (ymd && /^\d{8}$/.test(ymd)) {
    return moment(ymd, 'YYYYMMDD', true).format('L');
  }
  if (typeof raw === 'string') {
    return raw;
  }
  return '';
}
